import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Youtube, Eye, ArrowUpRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchVideos, cleanTitle, fmtViews, thumb, CHANNEL_URL } from "@/lib/youtube";

const ShortCard = ({ video, index }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: index * 0.07 }}>
    <Link to={`/videos?v=${video.id}`} className="group block" data-testid={`home-short-${video.id}`}>
      <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[var(--navy)] ring-1 ring-white/10 transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_24px_50px_-20px_rgba(255,0,51,0.45)]">
        <img src={thumb(video.id)} alt={cleanTitle(video.title)} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <span className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-white/90 text-[var(--navy)] flex items-center justify-center scale-90 group-hover:scale-100 group-hover:bg-[#ff0033] group-hover:text-white transition-[transform,background-color,color] duration-300">
          <Play className="w-5 h-5 ml-0.5 fill-current" />
        </span>
        <div className="absolute bottom-0 inset-x-0 p-3">
          <div className="text-white text-[12.5px] font-semibold leading-snug line-clamp-2">{cleanTitle(video.title)}</div>
          <div className="text-white/70 text-[11px] mt-1 inline-flex items-center gap-1"><Eye className="w-3 h-3" />{fmtViews(video.views)} views</div>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const YouTubeShorts = () => {
  const [shorts, setShorts] = useState(null);
  useEffect(() => {
    fetchVideos().then((v) => setShorts(v.filter((x) => x.short).slice(0, 6))).catch(() => setShorts([]));
  }, []);
  if (shorts && shorts.length === 0) return null;

  return (
    <section className="bg-[#fbf7ef] py-16 overflow-hidden" data-testid="home-youtube-strip">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[var(--yellow-2)]"><Youtube className="w-4 h-4 text-[#ff0033]" /> Latest from YouTube</div>
            <h2 className="font-serif-display text-3xl md:text-[40px] text-[var(--navy)] mt-2">Quick trips in 60 seconds</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`${CHANNEL_URL}?sub_confirmation=1`} target="_blank" rel="noreferrer" data-testid="home-youtube-subscribe" className="inline-flex items-center gap-2 rounded-full bg-[#ff0033] text-white px-4 py-2 text-[13px] font-semibold hover:bg-[#d9002b] transition-colors"><Youtube className="w-4 h-4" /> Subscribe</a>
            <Link to="/videos" data-testid="home-youtube-view-all" className="inline-flex items-center gap-2 rounded-full border border-[#0b2545]/25 text-[var(--navy)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--navy)] hover:text-white transition-colors">All videos <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        </div>
        <div className="flex lg:grid lg:grid-cols-6 gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 [scrollbar-width:none]">
          {(shorts || [...Array(6)]).map((v, i) => (
            <div key={v?.id || i} className="snap-start shrink-0 w-[44%] sm:w-[30%] lg:w-auto">
              {v ? <ShortCard video={v} index={i} /> : <Skeleton className="aspect-[9/16] rounded-2xl" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
